// Breakable glass panes of the map: one mesh per material holds every pane, so they all cost one draw call
// per material. A broken pane's triangles collapse to degenerate ones in the index buffer (a partial upload)
// until the panes are restored for the next round.

import * as THREE from "three";
import type { RenderMeshGroup } from "../wasm";

/** World-space box of a pane. */
export interface PaneBox {
  min: THREE.Vector3;
  max: THREE.Vector3;
}

interface Pane extends PaneBox {
  index: THREE.BufferAttribute;
  /** The group's intact index buffer. */
  original: Uint32Array;
  first: number;
  count: number;
  hidden: boolean;
}

const _p = new THREE.Vector3();

export class GlassPanes {
  readonly root = new THREE.Group();
  private readonly panes = new Map<number, Pane>();
  /** Every index buffer with its intact contents. */
  private readonly originals: { attr: THREE.BufferAttribute; indices: Uint32Array }[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private broken = 0;

  constructor(groups: readonly RenderMeshGroup[], materialFor: (name: string) => THREE.Material) {
    this.root.name = "glass";
    for (const g of groups) {
      const brushes = g.brushes;
      if (brushes === undefined || g.indices.length === 0) continue;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(g.positions, 3));
      geo.setAttribute("normal", new THREE.BufferAttribute(g.normals, 3));
      geo.setAttribute("uv", new THREE.BufferAttribute(g.uvs, 2));
      const index = new THREE.BufferAttribute(g.indices, 1);
      index.setUsage(THREE.DynamicDrawUsage);
      geo.setIndex(index);
      geo.computeBoundingSphere();
      this.geometries.push(geo);
      const original = g.indices.slice();
      this.originals.push({ attr: index, indices: original });
      for (let k = 0; k + 2 < brushes.length; k += 3) {
        const first = brushes[k + 1];
        const count = brushes[k + 2];
        const min = new THREE.Vector3(Infinity, Infinity, Infinity);
        const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        for (let i = first; i < first + count; i++) {
          const v = g.indices[i] * 3;
          min.min(_p.set(g.positions[v], g.positions[v + 1], g.positions[v + 2]));
          max.max(_p);
        }
        this.panes.set(brushes[k], { index, original, first, count, min, max, hidden: false });
      }
      const mesh = new THREE.Mesh(geo, materialFor(g.material));
      mesh.name = `glass:${g.material}`;
      mesh.matrixAutoUpdate = false;
      mesh.receiveShadow = true;
      this.root.add(mesh);
    }
  }

  /** Box of pane `brush`, or undefined when the map has no such pane. */
  box(brush: number): PaneBox | undefined {
    return this.panes.get(brush);
  }

  /** Stops drawing pane `brush`; false when there is no such pane or it is hidden already. */
  hide(brush: number): boolean {
    const p = this.panes.get(brush);
    if (p === undefined || p.hidden) return false;
    p.hidden = true;
    (p.index.array as Uint32Array).fill(0, p.first, p.first + p.count);
    this.upload(p);
    this.broken++;
    return true;
  }

  /** Draws pane `brush` again (a break shown ahead of the server that did not happen). */
  show(brush: number): void {
    const p = this.panes.get(brush);
    if (p === undefined || !p.hidden) return;
    p.hidden = false;
    (p.index.array as Uint32Array).set(p.original.subarray(p.first, p.first + p.count), p.first);
    this.upload(p);
    this.broken--;
  }

  /** Draws every pane again. */
  restoreAll(): void {
    if (this.broken === 0) return;
    this.broken = 0;
    for (const p of this.panes.values()) p.hidden = false;
    for (const { attr, indices } of this.originals) {
      (attr.array as Uint32Array).set(indices);
      attr.clearUpdateRanges();
      attr.needsUpdate = true;
    }
  }

  private upload(p: Pane): void {
    p.index.addUpdateRange(p.first, p.count);
    p.index.needsUpdate = true;
  }

  dispose(): void {
    this.root.removeFromParent();
    for (const g of this.geometries) g.dispose();
  }
}
